import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigationAction } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { Label } from '@pixacare/domain';
import { DepartmentResponse } from '@pixacare/dtos';
import { Observable } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { DepartmentHttpService } from 'src/app/services/http/department.http.service';
import { selectClient } from '../clients/clients.selectors';
import { labelsActions } from '../labels/labels.actions';
import { selectClientHasWriteAccess } from '../licenses/licenses.selectors';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { selectClientCode } from '../router/router.selectors';
import { departmentsActions } from './departments.actions';
import { selectDepartments } from './departments.selectors';

@Injectable()
export class DepartmentsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly departmentService = inject(DepartmentHttpService);
  private readonly translocoService = inject(TranslocoService);

  ensureClientDepartmentsOnNavigation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigationAction),
      withLatestFrom(this.store.select(selectClientCode)),
      filter(([, clientCode]) => !!clientCode),
      distinctUntilChanged(([, previousClientCode], [, clientCode]) => previousClientCode === clientCode),
      map(([, clientCode]) => departmentsActions.ensureClientDepartments({ clientCode })),
    ),
  );

  // Fetches a client's departments once its license is known, skipping clients already loaded.
  ensureClientDepartments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.ensureClientDepartments),
      mergeMap(({ clientCode }) =>
        this.store.select(selectClient(clientCode)).pipe(
          filter((client) => !!client),
          take(1),
          withLatestFrom(
            this.store.select(selectDepartments),
            this.store.select(selectClientHasWriteAccess(clientCode)),
          ),
          filter(([, departments]) => !departments[clientCode]),
          map(([, , hasWriteAccess]) =>
            hasWriteAccess
              ? departmentsActions.getDepartments({ clientCode })
              : departmentsActions.getDepartmentsSuccess({ clientCode, departments: [] }),
          ),
        ),
      ),
    ),
  );

  // Dedup concurrent fetches per client: the guard and the navigation effect can both
  // request the same client before its departments land, which would otherwise double-fetch.
  getDepartments$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.getDepartments),
      groupBy(({ clientCode }) => clientCode),
      mergeMap((clientDepartments$) =>
        clientDepartments$.pipe(
          exhaustMap(({ clientCode }) =>
            this.departmentService.getDepartments(clientCode).pipe(
              switchMap((departments: DepartmentResponse[]) => [
                departmentsActions.getDepartmentsSuccess({
                  clientCode,
                  departments,
                }),
                labelsActions.addLabels({
                  labels: departments.reduce<Label[]>(
                    (deptAcc, dept) => [
                      ...deptAcc,
                      ...dept.labelPacks.reduce<Label[]>(
                        (labelsAcc, labelPack) => [...labelsAcc, ...labelPack.labels],
                        [],
                      ),
                    ],
                    [],
                  ),
                }),
                labelsActions.addLabelPack({
                  labelPacks: departments.flatMap((department) => department.labelPacks),
                }),
              ]),
              // Mark the client's departments loaded even on failure so waiters
              // (e.g. the department admin guard) resolve instead of hanging.
              catchError((error: Error) => [
                departmentsActions.getDepartmentsSuccess({ clientCode, departments: [] }),
                lifeCycleActions.loadError({ error }),
              ]),
            ),
          ),
        ),
      ),
    ),
  );

  updateDepartment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.updateDepartment),
      mergeMap(({ clientCode, departmentId, name, isOpen, medicalSpecialtyId }) =>
        this.departmentService
          .updateDepartment({
            clientCode,
            departmentId,
            name,
            isOpen,
            medicalSpecialtyId,
          })
          .pipe(
            switchMap(() => [
              departmentsActions.updateDepartmentSuccess({
                clientCode,
                departmentId,
                name,
                isOpen,
                medicalSpecialtyId,
              }),
              notificationsActions.success({
                title: this.translocoService.translate('Groupe mis à jour'),
                message: this.translocoService.translate('Les informations du groupe ont bien été modifiées.'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate(`Une erreur est survenue.`),
                message: this.translocoService.translate("Nous n'avons pas pu modifier le groupe."),
              }),
            ]),
          ),
      ),
    ),
  );

  createDepartment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.createDepartment),
      mergeMap(({ clientCode, name }) =>
        this.departmentService.create(clientCode, name).pipe(
          mergeMap((department) => [
            departmentsActions.createDepartmentSuccess({
              clientCode,
              department,
            }),
            departmentsActions.getDepartmentMemberships({
              clientCode,
              departmentId: department.id,
            }),
            notificationsActions.success({
              title: this.translocoService.translate('Groupe créé'),
              message: this.translocoService.translate(`Le groupe {{ name }} a bien été créé.`, { name }),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu créer le groupe."),
            }),
          ]),
        ),
      ),
    ),
  );

  updateDepartmentUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.updateDepartmentUser),
      mergeMap(({ clientCode, departmentId, departmentUserId, membership }) =>
        this.departmentService
          .updateUser({
            clientCode,
            departmentId,
            departmentUserId,
            isAdmin: membership.isAdmin,
            disabledOn: membership.disabledOn,
          })
          .pipe(
            switchMap(() => [
              departmentsActions.updateDepartmentUserSuccess({
                clientCode,
                departmentId,
                departmentUserId,
                membership,
              }),
              notificationsActions.success({
                title: this.translocoService.translate('Utilisateur mis à jour'),
                message: this.translocoService.translate("Les informations de l'utilisateur ont bien été modifiées"),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate(`Une erreur est survenue.`),
                message: this.translocoService.translate("Nous n'avons pas pu modifier l'utilisateur."),
              }),
            ]),
          ),
      ),
    ),
  );
}
